import { BookOpenText, ListChecks, PenLine, Search, Settings } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useBibliotheque } from '@/api/hooks';
import { STATUTS_CONSULTATION, LIBELLE_STATUT_CONSULTATION, type ResumeProcedure, type StatutConsultation } from '@/api/types';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { StatutConsultationBadge } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr } from '@/lib/utils';

/** Cellule « Mon statut » : statut personnel de lecture, échéance ou confirmation ; vide si la procédure n'est pas applicable au lecteur. */
export function CelluleStatutPersonnel({ p }: { p: ResumeProcedure }) {
  if (!p.personnel?.concerne || !p.personnel.statut) return <span className="text-xs text-muted-foreground">non applicable à vous</span>;
  const s = p.personnel;
  return (
    <div className="space-y-1">
      <StatutConsultationBadge statut={s.statut as StatutConsultation} />
      <div className="text-xs text-muted-foreground">
        {s.statut === 'CONFIRMEE' && s.confirmeeLe ? `confirmée le ${dateFr(s.confirmeeLe)}` : s.premiereConsultation ? `consultée le ${dateFr(s.premiereConsultation)}` : s.echeance ? `${s.enRetard ? 'échéance dépassée' : 'à consulter avant'} le ${dateFr(s.echeance)}` : ''}
      </div>
    </div>
  );
}

/** Bibliothèque des procédures (spécifications démarches RH v1.4, § 6.1) : recherche, filtres, liste par domaine avec le statut personnel de lecture. */
export function Bibliotheque() {
  const [sp, setSp] = useSearchParams();
  const filtre = { q: sp.get('q') ?? undefined, domaineId: sp.get('domaineId') ?? undefined, structure: sp.get('structure') ?? undefined, statut: (sp.get('statut') as StatutConsultation | null) ?? undefined, depuis: sp.get('depuis') ?? undefined };
  const [q, setQ] = useState(filtre.q ?? '');
  const { data, isLoading, error } = useBibliotheque(filtre);
  const maj = (cle: string, valeur: string) => {
    const n = new URLSearchParams(sp);
    if (valeur) n.set(cle, valeur);
    else n.delete(cle);
    setSp(n);
  };
  if (isLoading && !data) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const structures = [...new Map(data.procedures.map((p) => [p.structureResponsable.code, p.structureResponsable])).values()].sort((a, b) => a.code.localeCompare(b.code));
  const domaines = data.domaines.filter((d) => data.procedures.some((p) => p.domaine.id === d.id));
  return (
    <div className="space-y-4">
      <PageHeader
        titre="Bibliothèque des procédures"
        sousTitre="Procédures publiées qui vous sont accessibles, classées par domaine. Lecture dans le lecteur intégré ; le statut « Lu » est enregistré après l'affichage effectif du document."
        actions={
          <div className="flex flex-wrap gap-2">
            {data.actions.suivre && (
              <Button asChild variant="outline" size="sm">
                <Link to="/procedures/suivi">
                  <ListChecks className="size-4" /> Suivi des consultations
                </Link>
              </Button>
            )}
            {data.actions.gerer && (
              <Button asChild variant="outline" size="sm">
                <Link to="/procedures/gestion">
                  <PenLine className="size-4" /> Gestion des procédures
                </Link>
              </Button>
            )}
            {data.actions.administrer && (
              <Button asChild variant="ghost" size="sm">
                <Link to="/administration/procedures">
                  <Settings className="size-4" /> Domaines et paramètres
                </Link>
              </Button>
            )}
          </div>
        }
      />
      <Card>
        <CardContent className="grid gap-3 pt-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="space-y-1 lg:col-span-2">
            <Label htmlFor="recherche">Recherche (titre, référence, mot-clé)</Label>
            <div className="flex gap-2">
              <Input id="recherche" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && maj('q', q.trim())} placeholder="congé, FED, sécurité…" />
              <Button type="button" variant="outline" size="icon" onClick={() => maj('q', q.trim())} aria-label="Rechercher">
                <Search className="size-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="f-domaine">Domaine</Label>
            <SelectNatif id="f-domaine" value={filtre.domaineId ?? ''} onChange={(e) => maj('domaineId', e.target.value)}>
              <option value="">Tous</option>
              {data.domaines.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1">
            <Label htmlFor="f-structure">Direction responsable</Label>
            <SelectNatif id="f-structure" value={filtre.structure ?? ''} onChange={(e) => maj('structure', e.target.value)}>
              <option value="">Toutes</option>
              {structures.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} · {s.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1">
            <Label htmlFor="f-statut">Mon statut de lecture</Label>
            <SelectNatif id="f-statut" value={filtre.statut ?? ''} onChange={(e) => maj('statut', e.target.value)}>
              <option value="">Tous</option>
              {STATUTS_CONSULTATION.map((s) => (
                <option key={s} value={s}>
                  {LIBELLE_STATUT_CONSULTATION[s]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1">
            <Label htmlFor="f-depuis">Publiées depuis le</Label>
            <Input id="f-depuis" type="date" value={filtre.depuis ?? ''} onChange={(e) => maj('depuis', e.target.value)} />
          </div>
        </CardContent>
      </Card>
      {!data.procedures.length ? (
        <Vide message="Aucune procédure ne correspond à ces critères." />
      ) : (
        domaines.map((d) => (
          <Card key={d.id} data-testid="domaine-procedures">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpenText className="size-5" /> {d.libelle}
              </CardTitle>
              <CardDescription>{data.procedures.filter((p) => p.domaine.id === d.id).length} procédure(s) publiée(s)</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Référence</TableHead>
                    <TableHead>Procédure</TableHead>
                    <TableHead>Responsable</TableHead>
                    <TableHead>Version en vigueur</TableHead>
                    <TableHead>Applicabilité</TableHead>
                    <TableHead>Mon statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.procedures
                    .filter((p) => p.domaine.id === d.id)
                    .map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">
                          <Link to={`/procedures/${p.id}`} className="text-primary hover:underline">
                            {p.reference}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link to={`/procedures/${p.id}`} className="font-medium hover:underline">
                            {p.titre}
                          </Link>
                          {p.description && <div className="line-clamp-2 text-xs text-muted-foreground">{p.description}</div>}
                          {p.confirmationRequise && (
                            <Badge variant="outline" className="mt-1">
                              confirmation requise
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {p.structureResponsable.code}
                          <div className="text-xs text-muted-foreground">{p.responsable.nom}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {p.versionEnVigueur ? (
                            <>
                              v{p.versionEnVigueur.numero}
                              <div className="text-xs text-muted-foreground">
                                publiée le {dateFr(p.versionEnVigueur.datePublication)}
                                {p.versionEnVigueur.dateEntreeVigueur ? ` · en vigueur le ${dateFr(p.versionEnVigueur.dateEntreeVigueur)}` : ''}
                              </div>
                            </>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={p.commune ? 'secondary' : 'outline'}>{p.etiquette}</Badge>
                        </TableCell>
                        <TableCell>
                          <CelluleStatutPersonnel p={p} />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
